import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { compressionCodecType, Dataset, DelimitedTextDataset, LinkedService, Metamodel, Named, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DelimitedTextSettings } from 'app/model/dataflow/dataset-settings/delimited-text-settings';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { ensureAllPathsAreUnique, getAdlsDatasetForPreview, initializeCommonConnectionProperties, processEachADLSEntity } from 'app/components/create-datamapper/utils/adls-component-utils';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { escapeDelimiter, unescapeDelimiter } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { delimitedTextEncodingNameOptionsForDataflow } from 'app/registries/consts/v2Dataset-const';
import { columnDelimiterV2Options, rowDelimiterV2Options, quoteCharV2Options, escapeCharV2Options } from 'app/shared/components/fileformat/fileformat.constants';
import { IDataMapperDelimitedFileOptions } from 'app/model/adfDataMapper/IDataMapperFileOptions';
import { levelOptions } from 'app/shared/components/compression-setting/compression-options';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';

export class DelimitedDatasetTypeHandler extends DatasetTypeHandler {

    constructor(
        public isSource: Boolean
    ) {
        super();
    }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('DelimitedTextSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as DelimitedTextSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as DelimitedTextSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.container.expression = !incomingSourceDatasetsSettings.container ? undefined : incomingSourceDatasetsSettings.container.expression;
        outputSourceDatasetsSettings.folderPath.expression = !incomingSourceDatasetsSettings.folderPath ? undefined : incomingSourceDatasetsSettings.folderPath.expression;
        outputSourceDatasetsSettings.compressionType = !incomingSourceDatasetsSettings.compressionType ? undefined : incomingSourceDatasetsSettings.compressionType;
        outputSourceDatasetsSettings.compressionLevel.expression = !incomingSourceDatasetsSettings.compressionLevel ? undefined : incomingSourceDatasetsSettings.compressionLevel.expression;
        outputSourceDatasetsSettings.columnDelimiter.expression = !incomingSourceDatasetsSettings.columnDelimiter ? undefined : unescapeDelimiter(incomingSourceDatasetsSettings.columnDelimiter.expression);
        outputSourceDatasetsSettings.rowDelimiter.expression = !incomingSourceDatasetsSettings.rowDelimiter ? undefined : unescapeDelimiter(incomingSourceDatasetsSettings.rowDelimiter.expression);
        outputSourceDatasetsSettings.escapeCharacter.expression = !incomingSourceDatasetsSettings.escapeCharacter ? undefined : unescapeDelimiter(incomingSourceDatasetsSettings.escapeCharacter.expression);
        outputSourceDatasetsSettings.quoteCharacter.expression = !incomingSourceDatasetsSettings.quoteCharacter ? undefined : unescapeDelimiter(incomingSourceDatasetsSettings.quoteCharacter.expression);
        outputSourceDatasetsSettings.encoding.expression = !incomingSourceDatasetsSettings.encoding ? undefined : incomingSourceDatasetsSettings.encoding.expression;
        outputSourceDatasetsSettings.columnNamesAsHeader.expression = 'true';
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        let disableButton = true;
        let disableButtonStatus = '';
        if (selectedRows.length) {
            disableButton = false;
            const rows = selectedRows as IMapperEntity[];
            rows.forEach((elem: IMapperEntity) => {
                const containerPath = (elem.dataset as DelimitedTextDataset)?.location?.getLocation()?.container;
                if (!containerPath) {
                    disableButton = true;
                }
            });
            if (!disableButton) {
                [disableButton, disableButtonStatus] = !ensureAllPathsAreUnique(rows) ? [true, this.isSource ? stringResources.DuplicateSourceEntitySelected : stringResources.DuplicateTargetEntitySelected] : [false, ''];
            }
            return [disableButton, disableButtonStatus];
        }
        return [disableButton, disableButtonStatus];
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            const filePath = (elem.dataset as DelimitedTextDataset).location.getLocation();
            processEachADLSEntity(filePath, selectedSourceLinkedService, elem, selectedDatasetFormat, tables, this.isSource);

        });

        return tables;
    }

    public getFileOptions(): IDataMapperDelimitedFileOptions {
        return {
            compressionTypeOptions: [
                {
                    label: stringResources.None,
                    value: undefined
                },
                {
                    label: compressionCodecType.bzip2,
                    value: compressionCodecType.bzip2
                },
                {
                    label: compressionCodecType.gzip,
                    value: compressionCodecType.gzip
                },
                {
                    label: compressionCodecType.deflate,
                    value: compressionCodecType.deflate
                },
                {
                    label: compressionCodecType.snappy,
                    value: compressionCodecType.snappy
                },
                {
                    label: compressionCodecType.lz4,
                    value: compressionCodecType.lz4
                }
            ],
            compressionLevelOptions: levelOptions,
            columnDelimiterV2Options,
            rowDelimiterV2Options,
            quoteCharV2Options,
            escapeCharV2Options,
            encodingOptions: delimitedTextEncodingNameOptionsForDataflow
        };
    }

    public getDefaultFileSettings(): DataFlowDatasetSettings {
        const dataFlowSource = DataFlowSource.MODEL.create<Named>();
        const delimitedSettings: DelimitedTextSettings = Owned.create<DelimitedTextSettings>(DelimitedTextSettings.MODEL, dataFlowSource);
        delimitedSettings.initialize(undefined);
        return delimitedSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const columnDelimiter = unescapeDelimiter(getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.columnDelimiter));
        const columnNamesAsHeader = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.columnNamesAsHeader);
        const compressionType = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionCodec);
        const compressionLevel = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionLevel);
        const encoding = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.encoding);
        const escapeChar = unescapeDelimiter(getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.escapeChar));
        const fileName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileName);
        const fileMode = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileMode);
        const fileSystem = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileSystem);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        const nullValue = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.nullValue);
        const quoteChar = unescapeDelimiter(getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.quoteChar));
        const rowDelimiter = unescapeDelimiter(getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.rowDelimiter));
        const wildcardPaths = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.wildcardPaths);

        // Create and populate DelimitedTextSettings
        const delimitedSettings: DelimitedTextSettings = Owned.create<DelimitedTextSettings>(DelimitedTextSettings.MODEL, table as DataFlowSource);
        delimitedSettings.initialize(undefined);

        table.datasetType = DatasetFormatType.Delimited;
        if (wildcardPaths && wildcardPaths.length > 0) {
            table.wildcardPaths = wildcardPaths.map((wp: string) => {
                const dfe = new DataFlowExpression();
                return dfe.expression = wp;
            });
        }

        delimitedSettings.compressionType = compressionType ?? undefined;
        delimitedSettings.container = createDataflowExpression(fileSystem) as DataFlowExpression;
        delimitedSettings.fileName = createDataflowExpression(fileName) as DataFlowExpression;
        delimitedSettings.fileMode = fileMode;
        delimitedSettings.folderPath = createDataflowExpression(folderPath) as DataFlowExpression;
        delimitedSettings.nullValue = createDataflowExpression(nullValue) as DataFlowExpression;
        if(!!columnDelimiter){
            delimitedSettings.columnDelimiter = createDataflowExpression(columnDelimiter) as DataFlowExpression;
        }
        if(!!columnNamesAsHeader){
            delimitedSettings.columnNamesAsHeader = createDataflowExpression(columnNamesAsHeader) as DataFlowExpression;
        }
        if(!!compressionLevel){
            delimitedSettings.compressionLevel = createDataflowExpression(compressionLevel) as DataFlowExpression;
        }
        if(!!escapeChar){
            delimitedSettings.escapeCharacter = createDataflowExpression(escapeChar) as DataFlowExpression;
        }
        if(!!quoteChar){
            delimitedSettings.quoteCharacter = createDataflowExpression(quoteChar) as DataFlowExpression;
        }
        if(!!encoding) {
            delimitedSettings.encoding = createDataflowExpression(encoding) as DataFlowExpression;
        }
        if(!!rowDelimiter) {
            delimitedSettings.rowDelimiter = createDataflowExpression(rowDelimiter) as DataFlowExpression;
        }

        return delimitedSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        return getAdlsDatasetForPreview(DatasetFormatType.Delimited, container, folderPath, linkedService);
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType, selectedSourceFileOptions: DataFlowDatasetSettings): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);
        initializeCommonConnectionProperties(mapperConnectionInfo.connection, selectedDatasetFormat, this.isSource);
        // Add delimited text dataset settings
        if (this.isSource) {
            const delimitedSettings = selectedSourceFileOptions as DelimitedTextSettings;
            if (!!delimitedSettings.compressionType) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.compressionCodec, delimitedSettings.compressionType);
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.compressionLevel, delimitedSettings.compressionLevel.expression);
            }
            if (!!delimitedSettings.encoding.expression) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.encodingName, delimitedSettings.encoding.expression);
            }
            if (!!delimitedSettings.rowDelimiter.expression) {
                mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.rowDelimiter, escapeDelimiter(delimitedSettings.rowDelimiter.expression));
            }
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.columnNamesAsHeader, true);
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.columnDelimiter, escapeDelimiter(delimitedSettings.columnDelimiter.expression));
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.quoteChar, escapeDelimiter(delimitedSettings.quoteCharacter.expression));
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.escapeChar, escapeDelimiter(delimitedSettings.escapeCharacter.expression));
        }
    }
}
